import { z } from 'zod';
import { GEOFENCE_MAX_METERS, GEOFENCE_MIN_METERS } from './catalog.js';
import { paginationQuerySchema } from './pagination.js';
import { zohoFailureReasonSchema } from './zoho.js';
export const ALERT_STATUSES = ['PENDING', 'VERIFIED', 'OVERRIDDEN', 'DISMISSED'];
/** BRANCH: a branch (maybe merchant) not in the catalog. FIELD_OPTION: an option typed for a multi-select field (e.g. a project). */
export const UNVERIFIED_ENTRY_KINDS = ['BRANCH', 'FIELD_OPTION'];
export const unverifiedBranchAlertDtoSchema = z.object({
    id: z.string(),
    status: z.enum(ALERT_STATUSES),
    kind: z.enum(UNVERIFIED_ENTRY_KINDS).default('BRANCH'),
    /** FIELD_OPTION: the form field the option was typed for. */
    field: z.object({ key: z.string(), label: z.string() }).nullable().default(null),
    /** The typed name: the branch (BRANCH) or the option, e.g. the project (FIELD_OPTION). */
    manualBranchName: z.string(),
    /** The merchant from the list, or null when the agent typed one (`manualBrandName`). */
    brand: z.object({ id: z.string(), name: z.string() }).nullable(),
    /** Typed by the agent when the merchant is not in the list either; verifying creates it. */
    manualBrandName: z.string().nullable(),
    addressNote: z.string().nullable(),
    agentNote: z.string().nullable(),
    /** GPS at the moment the agent reported the branch; falls back to the visit's check-out/check-in fix. */
    location: z
        .object({
        latitude: z.number(),
        longitude: z.number(),
        accuracyMeters: z.number().nullable(),
        source: z.enum(['REPORTED', 'CHECK_OUT', 'CHECK_IN']),
    })
        .nullable(),
    agent: z.object({ id: z.string(), fullName: z.string() }),
    visit: z.object({
        id: z.string(),
        submissionId: z.string(),
        startedAt: z.string(),
        zohoTicketNumber: z.string().nullable(),
        hasZohoTicket: z.boolean(),
        endLatitude: z.number().nullable(),
        endLongitude: z.number().nullable(),
    }),
    resolvedBy: z.object({ id: z.string(), fullName: z.string() }).nullable(),
    resolvedAt: z.string().nullable(),
    resolvedBranch: z.object({ id: z.string(), name: z.string() }).nullable(),
    resolutionNote: z.string().nullable(),
    /** English technical detail of the last failed Zoho correction. */
    zohoUpdateError: z.string().nullable(),
    /** Why that correction failed; null when none failed or it predates reason codes. Optional for older servers. */
    zohoUpdateErrorReasonCode: zohoFailureReasonSchema.nullable().optional(),
    createdAt: z.string(),
});
export const listAlertsQuerySchema = paginationQuerySchema.extend({
    status: z.enum(ALERT_STATUSES).optional(),
    kind: z.enum(UNVERIFIED_ENTRY_KINDS).optional(),
});
/** Add the typed branch to the catalog (optionally with a corrected name). */
export const verifyAlertRequestSchema = z.object({
    branchName: z.string().trim().min(1).max(120).optional(),
    /** Corrected spelling of a merchant the agent typed (only used when the alert has no listed merchant). */
    brandName: z.string().trim().min(1).max(120).optional(),
    branchCode: z.string().trim().min(1).max(30).nullable().default(null),
    geofenceRadiusMeters: z.number().int().min(GEOFENCE_MIN_METERS).max(GEOFENCE_MAX_METERS).optional(),
    /** Record the visit's end location as a location suggestion for the new branch. */
    suggestVisitLocation: z.boolean().default(true),
    note: z.string().trim().max(500).nullable().default(null),
});
/** Re-link the visit to an existing official branch and patch the Zoho ticket. */
export const overrideAlertRequestSchema = z.object({
    branchId: z.uuid(),
    note: z.string().trim().max(500).nullable().default(null),
});
export const dismissAlertRequestSchema = z.object({
    note: z.string().trim().max(500).nullable().default(null),
});
//# sourceMappingURL=alerts.js.map